import { useMemo } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px, SWReactCountryIcons } from "@similarweb/icons";
import type { ConversionRatesOverviewListData, ConversionRatesOverviewListParams } from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";
import {
    Badge,
    Card,
    ChangeValue,
    IconButton,
    Tooltip,
    TitlePlaceholderLoader,
    ListItemPlaceholderLoader,
} from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { openHooksModalV5 } from "app/services/ModalService";

import { CONVERSION_RATE_TRANSLATION_KEYS, conversionRateFormatters } from "./const";
import { ConversionRateEmptyState } from "./ConversionRateEmptyState";
import type { IConversionRateAvailability } from "./useConversionRateAvailability";
import { useConversionRateData } from "./useConversionRateData";

export type ConversionRateItem = ConversionRatesOverviewListData[0];

const StyledTitleLoader = styled(TitlePlaceholderLoader)`
    height: 24px;
`;

const StyledMainTitleLoader = styled(TitlePlaceholderLoader)`
    height: 40px;
`;

const StyledListItemLoader = styled(ListItemPlaceholderLoader)`
    height: 20px;
`;

const ConversionRateWidgetHeader = ({
    title,
    tooltip,
    dateRangeText,
    countryCode,
    countryText,
}: {
    title: string;
    tooltip: string;
    dateRangeText: string;
    countryCode: number;
    countryText: string;
}) => {
    const { durationTriggerProps, countryTriggerProps } = useSelectorTrigger("conversion-rate-widget");

    return (
        <Card.Header withDivider>
            <Card.Title>
                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    <Text
                        fontVariant="h5_2"
                        data-automation="conversion-rate-widget-title"
                    >
                        {title}
                    </Text>
                    <Flex alignItems="center">
                        <Badge
                            type="new"
                            data-automation="conversion-rate-widget-title-badge"
                        />
                        <Tooltip
                            content={tooltip}
                            data-automation="conversion-rate-widget-title-tooltip"
                        >
                            <Info24px
                                size={24}
                                color={colorsPalettes.carbon[200]}
                                data-automation="conversion-rate-widget-title-info-icon"
                            />
                        </Tooltip>
                    </Flex>
                </Flex>
                <Card.Query>
                    <Card.QueryItem
                        iconName="daily-ranking"
                        data-automation="conversion-rate-widget-date-range"
                        {...durationTriggerProps}
                    >
                        {dateRangeText}
                    </Card.QueryItem>
                    <Flex
                        gap="8px"
                        alignItems="center"
                        data-automation="conversion-rate-widget-country"
                        {...countryTriggerProps}
                    >
                        <SWReactCountryIcons
                            countryCode={countryCode}
                            size="xs"
                        />
                        <Text
                            fontVariant="chip1"
                            color={colorsPalettes.carbon[400]}
                        >
                            {countryText}
                        </Text>
                    </Flex>
                </Card.Query>
            </Card.Title>
        </Card.Header>
    );
};

interface IConversionRateWidgetProps {
    params: {
        country: number;
        from: string;
        to: string;
        key: string;
        webSource: string;
        isWindow: boolean;
        isWWW: boolean;
    };
    dateRangeText: string;
    countryText: string;
    countryCode: number;
    availability: IConversionRateAvailability;
}

export const ConversionRateWidget = ({
    params,
    dateRangeText,
    countryText,
    countryCode,
    availability,
}: IConversionRateWidgetProps) => {
    const { translate } = useTranslate();
    const { state: availabilityState, isLoading: isAvailabilityLoading } = availability;

    const apiParams = useMemo(
        () =>
            ({
                country: params.country,
                from: params.from,
                to: params.to,
                keys: [params.key],
                isWindow: params.isWindow,
                includeSubdomains: !params.isWWW,
            }) satisfies ConversionRatesOverviewListParams,
        [params.country, params.from, params.to, params.key, params.isWindow, params.isWWW],
    );

    const {
        data,
        isLoading: isDataLoading,
        isError,
        emptyStateType,
    } = useConversionRateData(apiParams, {
        enabled: availabilityState === "available",
    });

    const conversionRateItem = data?.[0];
    const hasData = !!conversionRateItem && conversionRateItem.conversionRate != null;
    const hasChange = hasData && conversionRateItem.change != null;
    const showEmptyState = isError || !hasData;

    const isLoading = isAvailabilityLoading || isDataLoading;

    if (availabilityState === "hidden") {
        return null;
    }

    if (availabilityState === "locked") {
        return (
            <Card data-automation="conversion-rate-widget">
                <ConversionRateWidgetHeader
                    title={translate(CONVERSION_RATE_TRANSLATION_KEYS.title)}
                    tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.tooltip)}
                    dateRangeText={dateRangeText}
                    countryCode={countryCode}
                    countryText={countryText}
                />
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    textAlign="center"
                    padding="24px"
                    gap="8px"
                    flex="1"
                >
                    <Text fontVariant="subtitle2">{translate(CONVERSION_RATE_TRANSLATION_KEYS.lockedTitle)}</Text>
                    <IconButton
                        type="trial"
                        iconName="sparkles"
                        onClick={() => openHooksModalV5("conversionRate")}
                    >
                        {translate(CONVERSION_RATE_TRANSLATION_KEYS.lockedCta)}
                    </IconButton>
                </Flex>
            </Card>
        );
    }

    if (isLoading) {
        return (
            <Card data-automation="conversion-rate-widget">
                <Card.Header>
                    <Card.Title>
                        <StyledTitleLoader />
                    </Card.Title>
                </Card.Header>
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    padding="16px 24px"
                    gap="8px"
                    flex="1"
                >
                    <StyledMainTitleLoader />
                    <StyledListItemLoader />
                </Flex>
            </Card>
        );
    }

    return (
        <Card data-automation="conversion-rate-widget">
            <ConversionRateWidgetHeader
                title={translate(CONVERSION_RATE_TRANSLATION_KEYS.title)}
                tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.tooltip)}
                dateRangeText={dateRangeText}
                countryCode={countryCode}
                countryText={countryText}
            />
            {showEmptyState ? (
                <ConversionRateEmptyState emptyStateType={emptyStateType} />
            ) : (
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    padding="16px 24px"
                    gap="8px"
                    flex="1"
                >
                    <Text
                        fontVariant="h2"
                        color={colorsPalettes.carbon[500]}
                        data-automation="conversion-rate-value"
                    >
                        {conversionRateFormatters.conversionRate(conversionRateItem?.conversionRate)}
                    </Text>
                    <Flex
                        gap="4px"
                        alignItems="center"
                    >
                        {hasChange ? (
                            <ChangeValue
                                value={conversionRateFormatters.change(conversionRateItem?.change)}
                                isDecrease={conversionRateItem?.change < 0}
                                unsigned={conversionRateItem?.change === 0}
                            />
                        ) : null}
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.carbon[300]}
                            data-automation="conversion-rate-change"
                        >
                            {translate(
                                hasChange
                                    ? CONVERSION_RATE_TRANSLATION_KEYS.changeText
                                    : CONVERSION_RATE_TRANSLATION_KEYS.changeNA,
                            )}
                        </Text>
                    </Flex>
                </Flex>
            )}
        </Card>
    );
};
